import { useContext, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react"
import AdHocCommands from "@/api/adHocCommands"
import { useAppSelector } from "@/reducers/hooks"
import { RootState } from "@/reducers/store"
import { selectAccountChatsAsList } from "@/reducers/accountChatsSlice"
import { driversProfiles } from "@/reducers/profilesSliceSelectors"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromJid } from "@/jidUtils"
import { normalizePlate } from "@/lib/plates"
import {
  buildCrewRows,
  buildRouteRows,
  driverDisplayName,
  driversWithoutCar,
  freeVehicles,
  isRouteStaffed,
  sortCrewRows,
  vehicleName,
  vehicleOfDriver,
  type CrewRow,
  type CrewSort,
  type CrewSortKey,
  type RouteRow,
} from "@/lib/crewBoard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useDriverNameOrder } from "@/hooks/useDriverNameOrder"
import { compareDriverNames, driverName } from "@/lib/driverNameOrder"
import { CrewCarCell } from "./CrewCarCell"
import { CrewDriverCell } from "./CrewDriverCell"
import { CrewRouteCell } from "./CrewRouteCell"
import { useTranslation } from "react-i18next"

const matches = (row: CrewRow, query: string): boolean => {
  const text = query.trim().toLowerCase()
  if (!text) return true
  const words = [
    row.sideNumber,
    row.route,
    row.car.label,
    row.car.plates,
    row.driver ? driverDisplayName(row.driver) : "",
  ]
  if (words.some((word) => word.toLowerCase().includes(text))) return true
  // Plates get typed with and without spaces or dashes ("DW 16", "dw-16").
  const token = normalizePlate(text)
  return (
    !!token &&
    [row.car.plates, row.car.label].some((word) =>
      normalizePlate(word).includes(token),
    )
  )
}

const routeMatches = ({ route, row }: RouteRow, query: string): boolean =>
  row
    ? matches(row, query)
    : route.toLowerCase().includes(query.trim().toLowerCase())

const countRoutes = (routeRows: RouteRow[]): number =>
  new Set(routeRows.map((routeRow) => routeRow.route)).size

type SortableHeadProps = {
  label: string
  column: CrewSortKey
  sort: CrewSort
  onSort: (column: CrewSortKey) => void
  className?: string
}

const SortableHead = ({
  label,
  column,
  sort,
  onSort,
  className,
}: SortableHeadProps) => {
  const active = sort.key === column
  const Icon = !active ? ArrowUpDown : sort.desc ? ArrowDown : ArrowUp
  return (
    <TableHead
      className={className}
      aria-sort={!active ? "none" : sort.desc ? "descending" : "ascending"}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => onSort(column)}
      >
        {label}
        <Icon
          className={`h-3.5 w-3.5 ${active ? "" : "text-muted-foreground/50"}`}
        />
      </button>
    </TableHead>
  )
}

export default function CrewBoard() {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  // Set for the whole account in Driver Management.
  const { order: nameOrder } = useDriverNameOrder()
  const cars = useAppSelector((state: RootState) => state.cars)
  const chats = useAppSelector(selectAccountChatsAsList)
  const profiles = useAppSelector(driversProfiles)
  const [query, setQuery] = useState("")
  const [onlyEmpty, setOnlyEmpty] = useState(false)
  const [sort, setSort] = useState<CrewSort>({ key: "sideNumber", desc: false })
  // The vhost's route codes from the wizard. None, or a wizard without the
  // command, means no Route column.
  const [routes, setRoutes] = useState<string[]>([])
  const [view, setView] = useState<"routes" | "vehicles">("routes")
  const byRoute = routes.length > 0 && view === "routes"

  useEffect(() => {
    let cancelled = false
    AdHocCommands.getRouteCodes(client)
      .then((codes) => {
        if (!cancelled) setRoutes(codes)
      })
      .catch(() => {
        if (!cancelled) setRoutes([])
      })
    return () => {
      cancelled = true
    }
  }, [client])

  const drivers = useMemo(() => {
    const myVhost = client?.jid
      ? extractVhostFromJid(client.jid.toString())
      : null
    return profiles
      .filter((p) => !p.disabled)
      .filter((p) => !myVhost || extractVhostFromJid(p.jid) === myVhost)
      .sort(compareDriverNames(nameOrder))
  }, [profiles, client?.jid, nameOrder])

  const rows = useMemo(
    // Seated drivers are looked up among all profiles: a disabled or foreign
    // one still sits in the car and must not read as "none".
    () => buildCrewRows(Object.values(cars), chats, profiles),
    [cars, chats, profiles],
  )
  const seats = useMemo(() => vehicleOfDriver(rows, chats), [rows, chats])
  const idle = useMemo(
    () => driversWithoutCar(chats, drivers),
    [chats, drivers],
  )

  const shownRows = useMemo(
    () =>
      sortCrewRows(
        rows.filter(
          (row) => (!onlyEmpty || !row.driver) && matches(row, query),
        ),
        sort,
        nameOrder,
      ),
    [rows, onlyEmpty, query, sort, nameOrder],
  )

  const routeRows = useMemo(() => buildRouteRows(routes, rows), [routes, rows])
  const shownRouteRows = useMemo(
    () =>
      routeRows.filter(
        (routeRow) =>
          (!onlyEmpty || !isRouteStaffed(routeRow)) &&
          routeMatches(routeRow, query),
      ),
    [routeRows, onlyEmpty, query],
  )
  const free = useMemo(() => freeVehicles(rows), [rows])

  const toggleSort = (key: CrewSortKey) =>
    setSort((current) => ({
      key,
      desc: current.key === key ? !current.desc : false,
    }))

  return createPortal(
    <div className="fixed inset-0 left-[49px] z-40 overflow-auto bg-background">
      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">
          {t("fleet.crewBoard.title")}
        </h1>
        <div className="mb-4 flex flex-wrap items-center gap-4">
          {routes.length > 0 && (
            <div className="inline-flex gap-1">
              <Button
                size="sm"
                variant={byRoute ? "default" : "outline"}
                aria-pressed={byRoute}
                onClick={() => setView("routes")}
              >
                {t("fleet.crewBoard.byRoute")}
              </Button>
              <Button
                size="sm"
                variant={byRoute ? "outline" : "default"}
                aria-pressed={!byRoute}
                onClick={() => setView("vehicles")}
              >
                {t("fleet.crewBoard.byVehicle")}
              </Button>
            </div>
          )}
          <div className="relative min-w-[280px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={
                routes.length
                  ? t("fleet.crewBoard.searchWithRoutes")
                  : t("fleet.crewBoard.search")
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="crew-board-only-empty"
              checked={onlyEmpty}
              onCheckedChange={setOnlyEmpty}
            />
            <Label htmlFor="crew-board-only-empty">
              {byRoute
                ? t("fleet.crewBoard.onlyUnstaffed")
                : t("fleet.crewBoard.onlyEmpty")}
            </Label>
          </div>
        </div>
        {byRoute && (
          <p className="mb-3 text-sm text-muted-foreground">
            {t("fleet.crewBoard.staffedCount", {
              staffed: countRoutes(routeRows.filter(isRouteStaffed)),
              total: countRoutes(routeRows),
            })}
          </p>
        )}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="flex-1 rounded-md border">
            {byRoute ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">
                      {t("fleet.crewBoard.route")}
                    </TableHead>
                    <TableHead className="w-[45%]">
                      {t("fleet.crewBoard.vehicle")}
                    </TableHead>
                    <TableHead>{t("fleet.crewBoard.driver")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownRouteRows.map((routeRow) => (
                    <TableRow key={routeRow.id}>
                      <TableCell className="font-medium">
                        {routeRow.route}
                      </TableCell>
                      <TableCell>
                        <CrewCarCell routeRow={routeRow} rows={rows} />
                      </TableCell>
                      <TableCell>
                        {routeRow.row ? (
                          <CrewDriverCell
                            row={routeRow.row}
                            drivers={drivers}
                            vehicleOfDriver={seats}
                          />
                        ) : (
                          <Button
                            variant="outline"
                            disabled
                            className="w-full justify-start font-normal"
                          >
                            {t("fleet.crewBoard.vehicleFirst")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    {routes.length > 0 && (
                      <SortableHead
                        label={t("fleet.crewBoard.route")}
                        column="route"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[140px]"
                      />
                    )}
                    <SortableHead
                      label={t("fleet.crewBoard.driver")}
                      column="driver"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[320px]"
                    />
                    <SortableHead
                      label={t("fleet.crewBoard.sideNumber")}
                      column="sideNumber"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[90px]"
                    />
                    <SortableHead
                      label={t("fleet.crewBoard.vehicle")}
                      column="model"
                      sort={sort}
                      onSort={toggleSort}
                    />
                    <SortableHead
                      label={t("fleet.crewBoard.plate")}
                      column="plates"
                      sort={sort}
                      onSort={toggleSort}
                      className="w-[130px]"
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownRows.map((row) => (
                    <TableRow key={row.car.id}>
                      {routes.length > 0 && (
                        <TableCell>
                          <CrewRouteCell row={row} routes={routes} />
                        </TableCell>
                      )}
                      <TableCell>
                        <CrewDriverCell
                          row={row}
                          drivers={drivers}
                          vehicleOfDriver={seats}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.sideNumber}
                      </TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell>{row.car.plates}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex flex-col gap-6 lg:w-[280px]">
            {byRoute && (
              <section
                aria-label={t("fleet.crewBoard.freeVehicles")}
                className="rounded-md border p-4"
              >
                <h2 className="mb-3 text-sm font-semibold">
                  {t("fleet.crewBoard.freeVehiclesCount", {
                    number: free.length,
                  })}
                </h2>
                <ul className="max-h-[40vh] space-y-1 overflow-y-auto text-sm">
                  {free.map((row) => (
                    <li key={row.car.id}>{vehicleName(row.car)}</li>
                  ))}
                </ul>
              </section>
            )}
            <section
              aria-label={t("fleet.crewBoard.idleDrivers")}
              className="rounded-md border p-4"
            >
              <h2 className="mb-3 text-sm font-semibold">
                {t("fleet.crewBoard.idleDriversCount", { number: idle.length })}
              </h2>
              <ul className="space-y-1 text-sm">
                {idle.map((driver) => (
                  <li key={driver.jid}>{driverName(driver, nameOrder)}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
